import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChildren,
  QueryList,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FilterAccordionComponent } from '../filter-accordion/filter-accordion.component';

export interface FilterOption {
  label: string;
  value: any;
  count?: number;
  selected?: boolean;
  disabled?: boolean;
  icon?: string;
}

export interface FilterConfig {
  key: string;
  title: string;
  type: 'checkbox' | 'radio' | 'input-range' | 'range';
  options?: FilterOption[];
  expanded?: boolean;
  isMulti?: boolean;
  minKey?: string;
  maxKey?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  minValue?: number;
  maxValue?: number;
  step?: number;
  initialValue?: number;
  showViewAll?: boolean;
  showSearch?: boolean;
  showCount?: boolean;
}

export interface FilterChange {
  key: string;
  value: any;
}

@Component({
  selector: 'app-filter-sidebar',
  templateUrl: './filter-sidebar.component.html',
  styleUrls: ['./filter-sidebar.component.scss'],
})
export class FilterSidebarComponent
  implements OnInit, AfterViewInit, OnChanges
{
  @Input() filters: FilterConfig[] = [];
  @Input() showSearchInput: boolean = true;
  @Input() searchPlaceholder: string = 'Search filters...';

  // @Output() filterChanged = new EventEmitter<FilterChange>();
  @Output() searchChanged = new EventEmitter<string>();
  @Output() clearAllFilters = new EventEmitter<void>();
  @Output() applyFiltersEvent = new EventEmitter<Record<string, any>>();

  @ViewChildren(FilterAccordionComponent)
  filterSections!: QueryList<FilterAccordionComponent>;

  searchQuery: string = '';
  filterValues: Record<string, any> = {};
  pendingFilterValues: Record<string, any> = {};
  private isBulkReset: boolean = false;
  private isInitializing: boolean = true;
  private hasFilterChanges: boolean = false;

  ngOnInit(): void {
    // Initialize component
  }

  ngAfterViewInit() {
    // Mark initialization as complete after components are ready
    setTimeout(() => {
      this.isInitializing = false;
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle changes to inputs
  }

  onFilterChange(change: FilterChange) {
    // Mark that filters have been changed
    this.hasFilterChanges = true;

    // Update the pending filter values (not the applied ones)
    if (change.value === null) {
      delete this.pendingFilterValues[change.key];
    } else {
      this.pendingFilterValues[change.key] = change.value;
    }

    // Don't emit filterChanged immediately - wait for apply button
    // Only emit during bulk reset or initialization
    if (this.isBulkReset || this.isInitializing) {
      if (change.value === null) {
        delete this.filterValues[change.key];
      } else {
        this.filterValues[change.key] = change.value;
      }
    }
  }

  resetFilters() {
    this.isBulkReset = true;

    // Reset all filter accordion components
    setTimeout(() => {
      if (this.filterSections) {
        this.filterSections.forEach((section) => {
          section.resetFilter();
        });
      }

      this.filterValues = {};
      this.pendingFilterValues = {};
      this.hasFilterChanges = false;
      this.isBulkReset = false;
      this.clearAllFilters.emit();
    }, 50);
  }

  applyFilters() {
    // Apply pending filter values
    this.filterValues = { ...this.pendingFilterValues };

    // Reset change tracking after applying
    this.hasFilterChanges = false;

    // Emit the applied filters
    this.applyFiltersEvent.emit(this.filterValues);
  }

  onSearchInput(event: Event) {
    const target = event.target as HTMLInputElement;
    this.searchQuery = target.value;
    this.searchChanged.emit(this.searchQuery);
  }

  clearFilters() {
    this.resetFilters();
  }

  get hasActiveFilters(): boolean {
    return Object.keys(this.filterValues).length > 0;
  }

  get hasSelectedFilters(): boolean {
    return this.hasFilterChanges;
  }

  get canReset(): boolean {
    return (
      Object.keys(this.filterValues).length > 0 ||
      Object.keys(this.pendingFilterValues).length > 0 ||
      this.hasFilterChanges
    );
  }

  /**
   * Set initial filter values (useful when loading from URL or parent component)
   * @param initialFilters The initial filter values to set
   */
  setInitialFilters(initialFilters: Record<string, any>): void {
    this.filterValues = { ...initialFilters };
    this.pendingFilterValues = { ...initialFilters };
  }
}
